<template>
  <div class="ai-visibility-review-step">
    <step-intro title="Refresh cost" flush>
      {{ refreshIntroText }}
      Each question adds {{ creditsPerQuestion }} {{ creditUnitLabel }} to the refresh cost
    </step-intro>

    <div class="ai-visibility-review-step__questions">
      <question-list-item
        v-for="question in reviewQuestions"
        :key="question.id"
        :question="question"
        :selected="isSelected(question.id)"
        @toggle="$emit('toggle-question', question)"
      />
    </div>

    <div class="ai-visibility-review-step__field">
      <label for="ai-visibility-refresh-frequency">Choose refresh frequency*</label>
      <div class="ai-visibility-review-step__select-wrap">
        <select
          id="ai-visibility-refresh-frequency"
          :value="refreshFrequency"
          :class="{ 'ai-visibility-review-step__select--error': validationErrors.refreshFrequency }"
          @change="$emit('update:refreshFrequency', $event.target.value)"
        >
          <option
            v-for="option in refreshFrequencyOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ getRefreshFrequencyOptionLabel(option) }}
          </option>
        </select>
        <i class="material-icons">keyboard_arrow_down</i>
      </div>
      <div v-if="validationErrors.refreshFrequency" class="ai-visibility-review-step__error">
        {{ validationErrors.refreshFrequency }}
      </div>
    </div>

    <div class="ai-visibility-review-step__cost">
      <p>
        <span>Questions selected: </span>
        <strong>{{ selectedQuestionCount }}</strong>
      </p>
      <p>
        <span>{{ frequencyCostLabel }} refresh cost: </span>
        <strong>
          {{ selectedQuestionCount }}
          {{ selectedQuestionCount === 1 ? 'question' : 'questions' }} +
          {{ baseRefreshCredits }} {{ creditUnitLabelPlural }}/{{ frequencyPeriodLabel.toLowerCase() }}
        </strong>
      </p>
      <p>
        <span>Total {{ frequencyCostLabel.toLowerCase() }} cost: </span>
        <strong>{{ finalCreditTotal }} {{ creditUnitLabelPlural }}/{{ frequencyPeriodLabel.toLowerCase() }}</strong>
      </p>
    </div>

    <div v-if="questionError" class="ai-visibility-review-step__error">
      {{ questionError }}
    </div>
  </div>
</template>

<script>
import { REFRESH_FREQUENCY_OPTIONS } from '../../constants'
import { getFinalCreditTotal } from '../../utils'
import QuestionListItem from '../ui/QuestionListItem.vue'
import StepIntro from '../ui/StepIntro.vue'

export default {
  name: 'ReviewStep',
  components: {
    QuestionListItem,
    StepIntro,
  },
  props: {
    reviewQuestions: {
      type: Array,
      default: () => [],
    },
    selectedQuestionIds: {
      type: Array,
      default: () => [],
    },
    refreshFrequency: {
      type: String,
      default: 'weekly',
    },
    creditPreview: {
      type: Object,
      required: true,
    },
    validationErrors: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    refreshFrequencyOptions () {
      return REFRESH_FREQUENCY_OPTIONS
    },
    selectedQuestionCount () {
      return this.selectedQuestionIds.length
    },
    activeFrequencyOption () {
      return this.refreshFrequencyOptions.find((option) => option.value === this.refreshFrequency) ||
        this.refreshFrequencyOptions[0]
    },
    frequencyPeriodLabel () {
      return this.activeFrequencyOption.periodLabel
    },
    frequencyCostLabel () {
      return this.activeFrequencyOption.label
    },
    isOnceFrequency () {
      return this.refreshFrequency === 'once'
    },
    refreshIntroText () {
      if (this.isOnceFrequency) {
        return 'We refresh your selected questions once.'
      }
      return `We refresh your selected questions once per ${this.frequencyPeriodLabel.toLowerCase()}.`
    },
    creditsPerQuestion () {
      return this.creditPreview.creditsPerQuestion
    },
    baseRefreshCredits () {
      return this.creditPreview.baseRefreshCredits
    },
    creditUnitLabel () {
      return this.creditPreview.unitLabel
    },
    creditUnitLabelPlural () {
      return this.finalCreditTotal === 1 ? this.creditUnitLabel : `${this.creditUnitLabel}s`
    },
    finalCreditTotal () {
      return getFinalCreditTotal(this.selectedQuestionIds, this.creditPreview)
    },
    questionError () {
      if (this.validationErrors.questions) {
        return this.validationErrors.questions
      }
      return this.selectedQuestionCount === 0 ? 'Select at least 1 question to start tracking.' : ''
    },
  },
  methods: {
    getRefreshFrequencyOptionLabel (option) {
      if (option.value === 'once') {
        return option.label
      }
      return `Every ${option.periodLabel.toLowerCase()}`
    },
    isSelected (questionId) {
      return this.selectedQuestionIds.includes(questionId)
    },
  },
}
</script>

<style lang="scss" scoped>

.ai-visibility-review-step {
  display: flex;
  min-height: 100%;
  flex-direction: column;
  gap: 20px;
}

.ai-visibility-review-step__questions {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.ai-visibility-review-step__field {
  display: flex;
  flex-direction: column;
  gap: 10px;

  label {
    color: var(--v-mainColor-base);
    font-size: 16px;
    line-height: 1.5;
    margin: 0;
  }
}

.ai-visibility-review-step__select-wrap {
  position: relative;

  select {
    width: 100%;
    height: 42px;
    appearance: none;
    -webkit-appearance: none;
    border: 1px solid #DADADA;
    border-radius: 4px;
    background: var(--v-cardBg-base);
    color: var(--v-mainColor-base);
    cursor: pointer;
    font-size: 14px;
    line-height: 22px;
    outline-color: #DADADA;
    padding: 10px 42px 10px 10px;
  }

  i {
    position: absolute;
    top: 50%;
    right: 10px;
    color: var(--v-mainColor3-base);
    font-size: 24px;
    pointer-events: none;
    transform: translateY(-50%);
  }
}

.ai-visibility-review-step__cost {
  display: flex;
  flex-direction: column;
  gap: 10px;
  color: var(--v-mainColor3-base);
  font-size: 14px;
  line-height: 1.4;
  margin-bottom: 20px;

  p {
    margin: 0;
  }

  strong {
    color: var(--v-mainColor-base);
    font-weight: 700;
  }
}

.ai-visibility-review-step__select--error {
  border-color: var(--v-red11Color-base) !important;
}

.ai-visibility-review-step__error {
  color: var(--v-red11Color-base);
  font-size: 12px;
  letter-spacing: 0.12px;
  text-align: center;
}
</style>
